import { useEffect, useState } from "react";

import type {
  NextPage,
  GetStaticProps,
  InferGetStaticPropsType,
  GetStaticPropsContext,
  GetServerSideProps,
  GetServerSidePropsContext,
} from "next";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";

import { FooterData, getFooterData } from "data/footer";
import { ProductData } from "data/product/products";

import {
  getProductData,
  getProductDataFromClassSlug,
  getAllProductPathsToPreRender,
  ProductPathObject,
  useUserProductPurchaseForProduct,
} from "~/api/productApi";
import { getStorefrontWithProducts } from "~/api/storefrontApi";
import { BTSInProductVideo } from "~/components/BTSInProductVideoPage";
import { RequireAuthWrapper } from "~/components/auth/RequireAuthWrapper";
import { useAllUserPurchaseFunctions } from "~/hooks/purchase/useUserPurchases";
import { isUUID } from "~/util/stringUtils";
import { getProductIdFromSlug, getPathForProduct } from "~/util/stringUtils";

interface StaticProps {
  productData: ProductData;
  footerData: FooterData;
  creatorDescription: string;
  productIdSlug: string;
  productId: string;
}

const BTSInProductVideoSSG: NextPage<InferGetStaticPropsType<typeof getServerSideProps>> = ({
  productData,
  footerData,
  creatorDescription,
  productIdSlug,
  productId,
}) => {
  const router = useRouter();
  const { handleRefetchUserPurchases } = useAllUserPurchaseFunctions();

  const [productIdInUrl, setProductIdInUrl] = useState(
    getProductIdFromSlug(router.query.productIdSlug as string),
  );

  const { isLoading, userProductPurchase, getUserProductPurchaseForProduct } =
    useUserProductPurchaseForProduct(productIdInUrl);

  const [userProductPurchaseToUse, setUserProductPurchaseToUse] = useState(userProductPurchase);
  useEffect(() => {
    router.events.on("routeChangeStart", handleRefetchUserPurchases);

    return () => {
      router.events.off("routeChangeStart", handleRefetchUserPurchases);
    };
  }, [router.events, handleRefetchUserPurchases]);

  useEffect(() => {
    const newProductId = getProductIdFromSlug(router.query.productIdSlug as string);
    setProductIdInUrl(newProductId);

    const fetchUserProductPurchase = async () => {
      try {
        const newUserPurchase = await getUserProductPurchaseForProduct(newProductId);
        setUserProductPurchaseToUse(newUserPurchase);
        // Do something with the data
      } catch (error) {
        // Handle error
      }
    };

    fetchUserProductPurchase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, productId, router]);

  if (router.isFallback) {
    return <div></div>;
  }

  if (!isLoading && !userProductPurchase) {

    router.push("/" + getPathForProduct(productData));
    return <div></div>;
  }

  return (
    <RequireAuthWrapper>
      {userProductPurchaseToUse ? (
        <BTSInProductVideo
          productData={userProductPurchaseToUse.productInfo}
          creatorDescription={creatorDescription}
          footerData={footerData}
        />
      ) : (
        <div></div>
      )}
    </RequireAuthWrapper>
  );
};

// export async function getStaticPaths() {
//   const productUrlSlugs = await getAllProductPathsToPreRender();

//   console.log("got the following productUrlSlugs from API: ", productUrlSlugs);

//   const paths = productUrlSlugs.map((product_path_object: ProductPathObject) => {
//     return {
//       params: {
//         productIdSlug: product_path_object.productIdSlug,
//       },
//     };
//   });

//   return {
//     paths: paths,
//     fallback: false,
//   };
// }

interface IParams extends ParsedUrlQuery {
  productIdSlug: string;
}

export const getServerSideProps: GetServerSideProps<StaticProps, IParams> = async (
  context: GetServerSidePropsContext<IParams>,
) => {
  // go fetch API, get data, pass down as props
  const productIdSlug = context.params?.productIdSlug;
  if (!productIdSlug) {
    throw new Error(`Failed to find Product`);
  }

  const productData = await getProductData(productIdSlug);
  console.log(
    "WATCH SERVERSIDE in here watch generating ",
    productIdSlug,
    productData.storefrontSlug,
  );
  const storefrontData = await getStorefrontWithProducts(productData.storefrontSlug);
  const creatorDescription = storefrontData.shortBio;

  //console.log("in here watch generating ", productData, storefrontData, creatorDescription)
  return {
    props: {
      productIdSlug: productIdSlug,
      productId: getProductIdFromSlug(productIdSlug),
      creatorDescription,
      productData: productData,
      footerData: getFooterData(),
    },
  };
};

export default BTSInProductVideoSSG;

/*
 STATIC PROPS VERSION
import { useEffect } from "react";

import type {
  NextPage,
  GetStaticProps,
  InferGetStaticPropsType,
  GetStaticPropsContext,
} from "next";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";

import { FooterData, getFooterData } from "data/footer";
import { ProductData } from "data/product/products";

import {
  getProductData,
  getProductDataFromClassSlug,
  getAllProductPathsToPreRender,
  ProductPathObject,
  useUserProductPurchaseForProduct,
} from "~/api/productApi";
import { getStorefrontWithProducts } from "~/api/storefrontApi";
import { BTSInProductVideo } from "~/components/BTSInProductVideoPage";
import { RequireAuthWrapper } from "~/components/auth/RequireAuthWrapper";
import { useAllUserPurchaseFunctions } from "~/hooks/purchase/useUserPurchases";
import { isUUID } from "~/util/stringUtils";
import { getProductIdFromSlug, getPathForProduct } from "~/util/stringUtils";

interface StaticProps {
  productData: ProductData;
  footerData: FooterData;
  creatorDescription: string;
}

const BTSInProductVideoSSG: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  productData,
  footerData,
  creatorDescription,
}) => {

  const router = useRouter();
  const { handleRefetchUserPurchases } = useAllUserPurchaseFunctions();

  useEffect(() => {
    router.events.on("routeChangeStart", handleRefetchUserPurchases);

    return () => {
      router.events.off("routeChangeStart", handleRefetchUserPurchases);
    };
  }, [router.events, handleRefetchUserPurchases]);

  const { isLoading, userProductPurchase } = useUserProductPurchaseForProduct(
    productData ? productData?.id : "",
  );



  if (router.isFallback) {
    return <div></div>;
  }

  if (!isLoading && !userProductPurchase) {

    router.push("/" + getPathForProduct(productData));
    return <div></div>;
  }

  return (
    <RequireAuthWrapper>
      {userProductPurchase ? (
        <BTSInProductVideo
          productData={productData}
          creatorDescription={creatorDescription}
          footerData={footerData}
        />
      ) : (
        <div></div>
      )}
    </RequireAuthWrapper>
  );
};

export async function getStaticPaths() {
  const productUrlSlugs = await getAllProductPathsToPreRender();



  const paths = productUrlSlugs.map((product_path_object: ProductPathObject) => {
    return {
      params: {
        productIdSlug: product_path_object.productIdSlug,
      },
    };
  });

  return {
    paths: paths,
    fallback: false,
  };
}

interface IParams extends ParsedUrlQuery {
  productIdSlug: string;
}

export const getStaticProps: GetStaticProps<StaticProps, IParams> = async (
  context: GetStaticPropsContext<IParams>,
) => {
  // go fetch API, get data, pass down as props
  const productIdSlug = context.params?.productIdSlug;
  if (!productIdSlug) {
    throw new Error(`Failed to find Product`);
  }

  const productData = await getProductData(productIdSlug);
  const storefrontData = await getStorefrontWithProducts(productData.storefrontSlug);
  const creatorDescription = storefrontData.description;

  //console.log("in here watch generating ", productData, storefrontData, creatorDescription)
  return {
    props: {
      creatorDescription,
      productData: productData,
      footerData: getFooterData(),
    },
  };
};

export default BTSInProductVideoSSG;


*/
